import cn from 'classnames'
import CheckmarkIcon from 'ext-ui/svg/icon/CheckmarkIcon'
import React, { ReactElement } from 'react'
import { useTranslate } from 'src/localization/Trns'

import style from './VirtualCardLimitMeter.style'

// Pro plan caps active VCs at 500 — the over-cap progress bar scales the
// fill against this so the user can see how far they are above the Starter
// target (100). Matches prototypes/extend-downgrade DowngradeGate.
const FROM_LIMIT = 500

interface VirtualCardLimitMeterProps {
  usage: number
  entitlement: number
  withinLimit: boolean
}

export default function VirtualCardLimitMeter(
  props: VirtualCardLimitMeterProps,
): ReactElement {
  const { usage, entitlement, withinLimit } = props
  const translate = useTranslate()

  const overBy = Math.max(0, usage - entitlement)
  // Over-cap: bar scales to the user's current plan limit (500 for Pro) with
  // a tick at the Starter target. Under-cap: bar scales to the Starter target.
  const fillPercent = withinLimit
    ? entitlement > 0
      ? Math.min(100, (usage / entitlement) * 100)
      : 0
    : Math.min(100, (usage / FROM_LIMIT) * 100)
  const tickPercent = (entitlement / FROM_LIMIT) * 100

  return (
    <div
      className={cn('VirtualCardLimitMeter', { 'over-limit': !withinLimit })}
      css={style}
      data-testid="virtual-card-limit-meter"
    >
      <div className="VirtualCardLimitMeter-row">
        <div className="VirtualCardLimitMeter-count-row">
          <span
            className="VirtualCardLimitMeter-count"
            data-testid="virtual-card-limit-meter-count"
          >
            {usage}
          </span>
          <span className="VirtualCardLimitMeter-limit-label">
            {translate('/${entitlement} Starter limit', {
              entitlement: String(entitlement),
            })}
          </span>
        </div>
        {withinLimit ? (
          <div
            className="VirtualCardLimitMeter-badge VirtualCardLimitMeter-badge--ok"
            data-testid="virtual-card-limit-meter-badge-within"
          >
            <CheckmarkIcon aria-hidden="true" />
            <span>{translate('Within Starter limit')}</span>
          </div>
        ) : (
          <div
            className="VirtualCardLimitMeter-badge VirtualCardLimitMeter-badge--over"
            data-testid="virtual-card-limit-meter-badge-over"
          >
            {translate('${count} over limit', { count: String(overBy) })}
          </div>
        )}
      </div>
      <div className="VirtualCardLimitMeter-bar-wrapper">
        <div className="VirtualCardLimitMeter-bar">
          <div
            className="VirtualCardLimitMeter-bar-fill"
            style={{ width: `${fillPercent}%` }}
          />
        </div>
        {!withinLimit && (
          <div
            className="VirtualCardLimitMeter-tick"
            style={{ left: `${tickPercent}%` }}
            data-testid="virtual-card-limit-meter-tick"
          >
            <div className="VirtualCardLimitMeter-tick-marker" />
            <span className="VirtualCardLimitMeter-tick-label">
              {entitlement}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
